import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { FinancialRecord, Category, ExchangeRate, RecordType, AccountType } from '../../types';
import CategoryDropdown from '../common/CategoryDropdown';
import '../../styles/RecordForm.css';

interface RecordFormProps {
  record: FinancialRecord | null;
  categories: Category[];
  currentRate: ExchangeRate | null;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const RecordForm: React.FC<RecordFormProps> = ({ record, categories, currentRate, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    record_date: new Date(),
    record_time: new Date(),
    description: '',
    category_id: '',
    amount_vnd: '',
    amount_usd: '',
    tax_percent: '0',
    record_type: 'expense' as RecordType,
    account_type: 'personal' as AccountType,
    exchange_rate_id: currentRate?.id || null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isCalculatingUsd, setIsCalculatingUsd] = useState(true);

  // When editing, populate form with record data
  useEffect(() => {
    if (record) {
      setFormData({
        record_date: new Date(record.record_date),
        record_time: record.record_time ? new Date(`2000-01-01T${record.record_time}`) : new Date(),
        description: record.description,
        category_id: record.category_id.toString(),
        amount_vnd: Math.abs(record.amount_vnd).toString(),
        amount_usd: Math.abs(record.amount_usd).toString(),
        tax_percent: record.tax_percent.toString(),
        record_type: record.record_type,
        account_type: record.account_type,
        exchange_rate_id: record.exchange_rate_id,
      });
    }
  }, [record]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    if (!formData.amount_vnd || isNaN(Number(formData.amount_vnd)) || Number(formData.amount_vnd) <= 0) {
      newErrors.amount_vnd = 'Valid amount in VND is required';
    }

    if (!formData.amount_usd || isNaN(Number(formData.amount_usd)) || Number(formData.amount_usd) <= 0) {
      newErrors.amount_usd = 'Valid amount in USD is required';
    }

    if (!formData.tax_percent || isNaN(Number(formData.tax_percent)) || Number(formData.tax_percent) < 0 || Number(formData.tax_percent) > 100) {
      newErrors.tax_percent = 'Tax percent must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Calculate USD when VND changes or vice versa
    if (name === 'amount_vnd' && currentRate && isCalculatingUsd) {
      const vndValue = parseFloat(value) || 0;
      const usdValue = vndValue / currentRate.vnd_to_usd;
      setFormData(prev => ({ ...prev, amount_usd: usdValue.toFixed(2) }));
    } else if (name === 'amount_usd' && currentRate && !isCalculatingUsd) {
      const usdValue = parseFloat(value) || 0;
      const vndValue = usdValue * currentRate.vnd_to_usd;
      setFormData(prev => ({ ...prev, amount_vnd: Math.round(vndValue).toString() }));
    }
  };

  const handleFocusAmount = (currency: 'vnd' | 'usd') => {
    setIsCalculatingUsd(currency === 'vnd');
  };

  const handleDateChange = (date: Date) => {
    setFormData(prev => ({ ...prev, record_date: date }));
  };

  const handleTimeChange = (time: Date) => {
    setFormData(prev => ({ ...prev, record_time: time }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validate()) {
      // Calculate tax amounts based on tax percent
      const amountVnd = parseFloat(formData.amount_vnd);
      const amountUsd = parseFloat(formData.amount_usd);
      const taxPercent = parseFloat(formData.tax_percent) / 100;
      
      const taxAmountVnd = Math.round(amountVnd * taxPercent);
      const taxAmountUsd = parseFloat((amountUsd * taxPercent).toFixed(2));
      
      // Convert form data to API format
      const recordData = {
        record_date: formData.record_date.toISOString().split('T')[0],
        record_time: formData.record_time.toTimeString().split(' ')[0].substring(0, 8),
        description: formData.description,
        category_id: parseInt(formData.category_id),
        // For expenses, store as negative amount
        amount_vnd: formData.record_type === 'expense' ? -amountVnd : amountVnd,
        amount_usd: formData.record_type === 'expense' ? -amountUsd : amountUsd,
        tax_percent: parseFloat(formData.tax_percent),
        tax_amount_vnd: formData.record_type === 'expense' ? -taxAmountVnd : taxAmountVnd,
        tax_amount_usd: formData.record_type === 'expense' ? -taxAmountUsd : taxAmountUsd,
        record_type: formData.record_type,
        account_type: formData.account_type,
        exchange_rate_id: formData.exchange_rate_id,
      };
      
      onSave(recordData);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-header">
          <h3 className="modal-title">{record ? 'Edit Record' : 'Create Record'}</h3>
          <button className="close-button" onClick={onCancel}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="record_type">Record Type</label>
                <div className="toggle-group">
                  <label className={`toggle-option ${formData.record_type === 'expense' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="record_type"
                      value="expense"
                      checked={formData.record_type === 'expense'}
                      onChange={handleChange}
                    />
                    <span>Expense</span>
                  </label>
                  <label className={`toggle-option ${formData.record_type === 'income' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="record_type"
                      value="income"
                      checked={formData.record_type === 'income'}
                      onChange={handleChange}
                    />
                    <span>Income</span>
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="account_type">Account Type</label>
                <div className="toggle-group">
                  <label className={`toggle-option ${formData.account_type === 'personal' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="account_type"
                      value="personal"
                      checked={formData.account_type === 'personal'}
                      onChange={handleChange}
                    />
                    <span>Personal</span>
                  </label>
                  <label className={`toggle-option ${formData.account_type === 'company' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="account_type"
                      value="company"
                      checked={formData.account_type === 'company'}
                      onChange={handleChange}
                    />
                    <span>Company</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="record_date">Date</label>
                <DatePicker
                  selected={formData.record_date}
                  onChange={handleDateChange}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  id="record_date"
                />
                {errors.record_date && <div className="error">{errors.record_date}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="record_time">Time</label>
                <DatePicker
                  selected={formData.record_time}
                  onChange={handleTimeChange}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  className="form-control"
                  id="record_time"
                />
                {errors.record_time && <div className="error">{errors.record_time}</div>}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                name="description"
                id="description"
                value={formData.description}
                onChange={handleChange}
                className="form-control"
                rows={3}
              ></textarea>
              {errors.description && <div className="error">{errors.description}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="category_id">Category</label>
              <CategoryDropdown
                categories={categories.filter(cat => 
                  cat.type === formData.record_type && 
                  cat.account_type === formData.account_type
                )}
                value={formData.category_id}
                onChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                placeholder="Select a category"
                className="form-control"
                title="Select category (hover for descriptions)"
              />
              {errors.category_id && <div className="error">{errors.category_id}</div>}
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="amount_vnd">Amount (VND)</label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    name="amount_vnd"
                    id="amount_vnd"
                    value={formData.amount_vnd}
                    onChange={handleChange}
                    onFocus={() => handleFocusAmount('vnd')}
                    className="form-control"
                  />
                  <span className="input-icon">₫</span>
                </div>
                {errors.amount_vnd && <div className="error">{errors.amount_vnd}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="amount_usd">Amount (USD)</label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    name="amount_usd"
                    id="amount_usd"
                    value={formData.amount_usd}
                    onChange={handleChange}
                    onFocus={() => handleFocusAmount('usd')}
                    className="form-control"
                  />
                  <span className="input-icon">$</span>
                </div>
                {errors.amount_usd && <div className="error">{errors.amount_usd}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="tax_percent">Tax %</label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    name="tax_percent"
                    id="tax_percent"
                    value={formData.tax_percent}
                    onChange={handleChange}
                    className="form-control"
                  />
                  <span className="input-icon">%</span>
                </div>
                {errors.tax_percent && <div className="error">{errors.tax_percent}</div>}
              </div>
            </div>
            
            {currentRate && (
              <div className="exchange-rate-info">
                <p>Current Exchange Rate: 1 USD = {currentRate.vnd_to_usd.toLocaleString()} VND</p>
                <p className="rate-date">Effective: {new Date(currentRate.effective_date).toLocaleDateString()}</p>
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save Record</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordForm;
